export const UserRole = {
  ADMIN: 'admin',
  USER: 'user',
  VIEWER: 'viewer',
  EXTERNAL: 'external',   // planTEK: restricted to assigned external views only
} as const;

export type UserRole = typeof UserRole[keyof typeof UserRole];

export const Permission = {
  READ: 'read',
  WRITE: 'write',
  DELETE: 'delete',
  ADMIN: 'admin'
} as const;

export type Permission = typeof Permission[keyof typeof Permission];

export interface TenantInfo {
  tenant_id: string;
  tenant_name: string;
  plan: string;
  is_active: boolean;
  // R2: tenant-level MCP gate. The self-serve Claude-connector affordance renders
  // ONLY when this is true AND the user has the `ai_connector` module — the same
  // two-gate AND the backend mint endpoint enforces. Optional/undefined => gated off.
  mcp_access?: boolean;
}

// Available tenant for switcher dropdown (9.32)
export interface AvailableTenant {
  tenant_id: string;
  name: string;
  role?: string;
}

export interface UserModules {
  user_management?: boolean | string[];
  settings?: boolean | 'read_only';
  templates?: boolean | 'read_only';
  reporting?: boolean | 'read_only';
  can_delete_projects?: boolean;
  can_delete_invoices?: boolean;
  can_send_emails?: boolean;
  [key: string]: boolean | string | string[] | undefined;
}

export interface AuthUser {
  user_id: string;
  email: string;
  name: string;
  role: UserRole;
  permissions: Permission[];
  tenant_id: string;
  tenant: TenantInfo;
  available_tenants?: AvailableTenant[]; // Tenants user can switch to (9.32)
  is_active: boolean;
  last_login: string;
  modules?: UserModules;
  is_admin?: boolean;
  is_super_admin?: boolean; // Global super-admin access (9.32)
}

export interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

export interface AuthContextType extends AuthState {
  signIn: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  createUserWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
  clearError: () => void;
  can: (permission: string) => boolean;
  isReadOnly: (permission: string) => boolean;
  // SPEC-connector-hardening R5 AC3: re-hydrate `user` from GET /auth/me without a
  // full reload. Called on window focus and after an admin grant/revoke mutation so
  // the connector card + AI-Connector toggle reflect grant/revoke changes promptly
  // (AuthProvider previously hydrated from /auth/me exactly once — the stale gate).
  refreshUser: () => Promise<void>;
}